/* eslint-disable react/no-unescaped-entities --
 * v77.99.86: Upcoming-expense heads-up UI. Refactored from a single
 * "section" into a dual-mode component so it can drive both the
 * compact Expenses-page summary card AND the dedicated /app/upcoming-
 * expenses planning page without duplicating the create/edit dialog,
 * convert + mark-paid flows, or the PDF export.
 *
 *   <UpcomingExpensesSection variant="summary" ... />  ← Expenses page
 *   <UpcomingExpensesSection variant="page"    ... />  ← Dedicated page
 *
 * The imperative ref (`openCreate()`) still works in either variant,
 * so the "Flag upcoming expense" buttons on the Expenses page and the
 * new dedicated page both wire to the same dialog.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useChildScope } from "@/context/ChildScopeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  CalendarClock, Plus, Trash2, CheckCircle2, Share2, EyeOff, Pencil,
  ArrowRightCircle, Download, FileCheck2, ExternalLink, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { api, API, getToken } from "@/lib/api";
import { downloadFile } from "@/lib/downloadFile";

const EMPTY_FORM = {
  title: "",
  category: "other",
  amount: "",
  expected_date: "",
  notes: "",
  child_ids: [],
  shared_with_coparent: false,
  reminder_date: "",
  show_on_calendar: false,
};

const CATEGORIES = [
  { value: "school",          label: "School / childcare" },
  { value: "medical",         label: "Medical / dental" },
  { value: "extra-curricular", label: "Sports / clubs / camps" },
  { value: "clothing",        label: "Clothing / uniform" },
  { value: "tech",            label: "Tech / devices" },
  { value: "travel",          label: "Travel / holiday" },
  { value: "other",           label: "Other" },
];

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
  if (Number.isNaN(d.getTime())) return null;
  const days = Math.round((d.getTime() - Date.now()) / 86400000);
  if (days < 0) return `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} overdue`;
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days < 30) return `in ${days} days`;
  if (days < 90) return `in ${Math.round(days / 7)} weeks`;
  return `in ${Math.round(days / 30)} months`;
}

/**
 * Dual-mode upcoming-expense component (see file header).
 *
 * @param {"summary"|"page"} variant
 * @param {string} currency
 * @param {Array<{id:string, name:string}>} childList
 * @param {boolean} isMediator
 * @param {(item, onLinked) => void} [onConvertReady] — invoked when the
 *   user picks "Convert to expense"; the host page wires this to its
 *   own Add-Expense flow. Only meaningful in `variant="page"`.
 */
export const UpcomingExpensesSection = forwardRef(function UpcomingExpensesSection(
  { variant = "summary", currency = "GBP", childList = [], onConvertReady, isMediator },
  ref,
) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // v77.99.96 — Honour the active ChildScope (set via the
  // /app sidebar / mobile pill switcher) so the dedicated Upcoming
  // Expenses page filters records per the pinned child instead of
  // always returning the household-wide list. Master view (null
  // selectedChildId) keeps the existing all-children behaviour.
  const { selectedChildId } = useChildScope();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    child_id: "", from_date: "", to_date: "",
    visibility: "all", category: "all", status: "upcoming",
  });

  const load = useCallback(async () => {
    try {
      const params = selectedChildId ? { params: { child_id: selectedChildId } } : undefined;
      const { data } = await api.get("/upcoming-expenses", params);
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [selectedChildId]);

  useEffect(() => { load(); }, [load]);

  const moneyFmt = useMemo(() => new Intl.NumberFormat(undefined, {
    style: "currency", currency: currency || "GBP",
  }), [currency]);

  function resetForm() {
    setForm({
      ...EMPTY_FORM,
      // Default expected date to today+30 so the user just tweaks.
      expected_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
      // v77.99.96 — When a specific child is pinned in the
      // ChildScope, auto-link new heads-ups to them. Master view
      // (selectedChildId=null) leaves child_ids empty so the user
      // picks explicitly.
      child_ids: selectedChildId ? [selectedChildId] : [],
    });
  }

  function openCreate() {
    setEditingId(null);
    resetForm();
    setCreateOpen(true);
  }

  function openEdit(item) {
    setEditingId(item.id);
    setForm({
      title: item.title || "",
      category: item.category || "other",
      amount: String(item.amount || ""),
      expected_date: item.expected_date || "",
      notes: item.notes || "",
      child_ids: item.child_ids || [],
      shared_with_coparent: !!item.shared_with_coparent,
      reminder_date: item.reminder_date || "",
      show_on_calendar: !!item.show_on_calendar,
    });
    setCreateOpen(true);
  }

  useImperativeHandle(ref, () => ({ openCreate, reload: load }), [load]);

  async function handleSave() {
    if (!form.title.trim()) { toast.error("Give it a short title."); return; }
    const amount = Number(form.amount);
    if (!amount || amount <= 0) { toast.error("Enter the estimated amount."); return; }
    if (!form.expected_date) { toast.error("Pick when you expect to pay."); return; }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        category: form.category,
        amount,
        expected_date: form.expected_date,
        notes: form.notes.trim(),
        child_ids: form.child_ids,
        shared_with_coparent: form.shared_with_coparent,
        reminder_date: form.reminder_date || null,
        show_on_calendar: form.show_on_calendar,
      };
      if (editingId) {
        await api.patch(`/upcoming-expenses/${editingId}`, payload);
        toast.success("Upcoming expense updated.");
      } else {
        await api.post("/upcoming-expenses", payload);
        toast.success("Upcoming expense logged.", {
          description: form.shared_with_coparent
            ? "Your co-parent will see it on their dashboard."
            : "Private — only you can see it.",
        });
      }
      setCreateOpen(false);
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error("Couldn't save heads-up.", { description: e?.response?.data?.detail || "" });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(item) {
    if (!window.confirm(`Delete the heads-up for "${item.title}"?`)) return;
    try {
      await api.delete(`/upcoming-expenses/${item.id}`);
      toast.success("Heads-up removed.");
      await load();
    } catch (e) {
      toast.error("Couldn't delete.", { description: e?.response?.data?.detail || "" });
    }
  }

  function handleConvert(item) {
    if (item.converted_to_expense_id) { toast.info("Already converted to an expense."); return; }
    if (item.marked_paid_at) { toast.info("Already marked as paid — can't convert."); return; }
    if (typeof onConvertReady === "function") {
      onConvertReady(item, async (newExpenseId) => {
        try {
          await api.post(`/upcoming-expenses/${item.id}/convert`, { expense_id: newExpenseId });
          toast.success("Converted — saved as a tracked expense.");
          await load();
        } catch (e) {
          toast.error("Couldn't link expense.", { description: e?.response?.data?.detail || "" });
        }
      });
    } else {
      toast.info("Open the Upcoming Expenses page to convert.");
    }
  }

  async function handleMarkPaid(item) {
    if (item.converted_to_expense_id || item.marked_paid_at) { toast.info("Already settled."); return; }
    if (!window.confirm(`Mark "${item.title}" as paid? It will stay listed with a "Paid (no expense)" badge. Use "Convert to expense" instead if you want a tracked receipt.`)) return;
    try {
      await api.post(`/upcoming-expenses/${item.id}/mark-paid`);
      toast.success("Marked as paid.");
      await load();
    } catch (e) {
      toast.error("Couldn't mark as paid.", { description: e?.response?.data?.detail || "" });
    }
  }

  function toggleChild(cid) {
    setForm((f) => ({
      ...f,
      child_ids: f.child_ids.includes(cid)
        ? f.child_ids.filter((x) => x !== cid)
        : [...f.child_ids, cid],
    }));
  }

  function handleExport() {
    const params = new URLSearchParams();
    const ef = exportFilters;
    if (ef.child_id) params.set("child_id", ef.child_id);
    if (ef.from_date) params.set("from_date", ef.from_date);
    if (ef.to_date) params.set("to_date", ef.to_date);
    if (ef.visibility && ef.visibility !== "all") params.set("visibility", ef.visibility);
    if (ef.category && ef.category !== "all") params.set("category", ef.category);
    if (ef.status && ef.status !== "all") params.set("status", ef.status);
    // Iter 125.hybrid-exports — routed through the shared `downloadFile`
    // helper so we get: (1) proper auth (cookie + Authorization header),
    // (2) the "Generating…" toast, (3) the hybrid 15s / 30s email
    // fallback that queues the PDF in the background if the sync
    // build stalls. Replaces the earlier `window.open(url, "_blank")`
    // which relied purely on cookies and left the user with a broken
    // tab if the render was slow.
    const url = `${API}/upcoming-expenses/export/pdf?${params.toString()}`;
    const today = new Date().toISOString().slice(0, 10);
    downloadFile(url, `upcoming-expenses-${today}.pdf`, {
      label: "Generating upcoming-expenses PDF",
      successMessage: "PDF ready",
      asyncFallback: {
        endpoint: "/exports/upcoming-expenses/pdf/start",
        payload: {
          child_id: ef.child_id || null,
          from_date: ef.from_date || null,
          to_date: ef.to_date || null,
          visibility: (ef.visibility && ef.visibility !== "all") ? ef.visibility : null,
          category: (ef.category && ef.category !== "all") ? ef.category : null,
          status: (ef.status && ef.status !== "all") ? ef.status : null,
        },
      },
    });
    setExportOpen(false);
  }

  // ============================ Render helpers ============================

  const activeItems = items.filter((i) => !i.converted_to_expense_id && !i.marked_paid_at);
  const totalEstimated = activeItems.reduce((s, i) => s + (Number(i.amount) || 0), 0);

  const CreateEditDialog = (
    <Dialog open={createOpen} onOpenChange={(v) => { setCreateOpen(v); if (!v) setEditingId(null); }}>
      <DialogContent className="sm:max-w-[480px]" data-testid="upcoming-expense-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {editingId ? "Edit upcoming expense" : "New upcoming expense"}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#6F828A]">
            Estimate the cost and when you'll pay it. Share with your co-parent if you'd like them to see it on their dashboard.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <div>
            <Label htmlFor="ue-title">Title</Label>
            <Input id="ue-title" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Summer camp deposit"
              data-testid="upcoming-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ue-amount">Estimated amount</Label>
              <Input id="ue-amount" type="number" step="0.01" min="0"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0.00"
                data-testid="upcoming-amount-input" />
            </div>
            <div>
              <Label htmlFor="ue-date">Expected date</Label>
              <Input id="ue-date" type="date"
                value={form.expected_date}
                onChange={(e) => setForm({ ...form, expected_date: e.target.value })}
                data-testid="upcoming-date-input" />
            </div>
          </div>
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="upcoming-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          {childList.length > 0 && (
            <div>
              <Label>Children</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {childList.map((c) => {
                  const active = form.child_ids.includes(c.id);
                  return (
                    <button type="button" key={c.id} onClick={() => toggleChild(c.id)}
                      className={`px-3 py-1 rounded-full text-xs border ${active ? "bg-[#116B6E] text-white border-[#116B6E]" : "border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#F0F8F8]"}`}
                      data-testid={`upcoming-child-${c.id}`}>
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ue-reminder">Reminder date <span className="text-[10px] text-[#6F828A]">(optional)</span></Label>
              <Input id="ue-reminder" type="date"
                value={form.reminder_date}
                onChange={(e) => setForm({ ...form, reminder_date: e.target.value })}
                data-testid="upcoming-reminder-input" />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm text-[#2E3A3F]">
                <Switch checked={form.show_on_calendar}
                  onCheckedChange={(v) => setForm({ ...form, show_on_calendar: v })}
                  data-testid="upcoming-calendar-toggle" />
                Show on calendar
              </label>
            </div>
          </div>
          <div>
            <Label htmlFor="ue-notes">Notes</Label>
            <Textarea id="ue-notes" rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Optional context for yourself or your co-parent."
              data-testid="upcoming-notes-input" />
          </div>
          <div className="flex items-center justify-between rounded-md border border-[#D9E1E3] bg-white px-3 py-2.5">
            <div className="pr-3">
              <div className="text-sm font-medium text-[#2E3A3F]">Share with co-parent</div>
              <div className="text-[11px] text-[#6F828A] leading-snug mt-0.5">
                Surfaces on their dashboard as a heads-up. They can't edit it.
              </div>
            </div>
            <Switch checked={form.shared_with_coparent}
              onCheckedChange={(v) => setForm({ ...form, shared_with_coparent: v })}
              data-testid="upcoming-share-toggle" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => { setCreateOpen(false); setEditingId(null); }} disabled={submitting} data-testid="upcoming-cancel-btn">Cancel</Button>
          <Button onClick={handleSave} disabled={submitting} className="bg-[#1F9FA4] hover:bg-[#116B6E] text-white" data-testid="upcoming-submit-btn">
            {submitting ? "Saving…" : (editingId ? "Save changes" : "Save heads-up")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const ExportDialog = (
    <Dialog open={exportOpen} onOpenChange={setExportOpen}>
      <DialogContent className="sm:max-w-[480px]" data-testid="upcoming-export-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Export upcoming expenses</DialogTitle>
          <DialogDescription className="text-sm text-[#6F828A]">
            Download a PDF report of your heads-up records. Pick filters or leave them blank for everything.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {childList.length > 0 && (
            <div>
              <Label>Child</Label>
              <Select value={exportFilters.child_id || "all"}
                onValueChange={(v) => setExportFilters({ ...exportFilters, child_id: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="export-child-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All children</SelectItem>
                  {childList.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="exp-from">From</Label>
              <Input id="exp-from" type="date" value={exportFilters.from_date}
                onChange={(e) => setExportFilters({ ...exportFilters, from_date: e.target.value })}
                data-testid="export-from-input" />
            </div>
            <div>
              <Label htmlFor="exp-to">To</Label>
              <Input id="exp-to" type="date" value={exportFilters.to_date}
                onChange={(e) => setExportFilters({ ...exportFilters, to_date: e.target.value })}
                data-testid="export-to-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Visibility</Label>
              <Select value={exportFilters.visibility} onValueChange={(v) => setExportFilters({ ...exportFilters, visibility: v })}>
                <SelectTrigger data-testid="export-visibility-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="shared">Shared only</SelectItem>
                  <SelectItem value="private">Private only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={exportFilters.status} onValueChange={(v) => setExportFilters({ ...exportFilters, status: v })}>
                <SelectTrigger data-testid="export-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="upcoming">Upcoming only</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                  <SelectItem value="paid">Marked paid</SelectItem>
                  <SelectItem value="all">All statuses</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Category</Label>
            <Select value={exportFilters.category} onValueChange={(v) => setExportFilters({ ...exportFilters, category: v })}>
              <SelectTrigger data-testid="export-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {CATEGORIES.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setExportOpen(false)} data-testid="export-cancel-btn">Cancel</Button>
          <Button onClick={handleExport} className="bg-[#1F9FA4] hover:bg-[#116B6E] text-white" data-testid="export-download-btn">
            <Download className="w-4 h-4 mr-1.5" /> Download PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // ============================ SUMMARY VARIANT ============================
  // Lives on /app/expenses. Hidden entirely when there are zero items
  // so it never adds visual noise to the expense tracking flow.
  if (variant === "summary") {
    if (loading) return null;
    if (items.length === 0) {
      // Still render the dialogs (offscreen) so the imperative
      // openCreate() from the page's top-bar button works even when
      // the summary card itself isn't visible.
      return <>{CreateEditDialog}</>;
    }
    return (
      <>
        <Link
          to="/app/upcoming-expenses"
          className="block border border-[#D9E1E3] bg-[#FBFCFC] hover:bg-[#F5F8F8] rounded-md mb-5 px-4 py-3 sm:py-3.5 transition-colors"
          data-testid="upcoming-summary-card"
        >
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#FBF0DC] flex items-center justify-center">
              <CalendarClock className="w-4 h-4 text-[#B88A3A]" aria-hidden />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-medium text-[#2E3A3F]">Upcoming Expenses</span>
                <span
                  className="text-xs sm:text-sm text-[#6F828A]"
                  data-testid="upcoming-summary-line"
                >
                  {activeItems.length === 0
                    ? `${items.length} settled item${items.length === 1 ? "" : "s"}`
                    : `${activeItems.length} flagged · ${moneyFmt.format(totalEstimated)} estimated`}
                </span>
              </div>
              <p className="text-[11px] text-[#6F828A] mt-0.5">
                Track expected costs before they become real expenses.
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-[#6F828A] flex-shrink-0" aria-hidden />
          </div>
        </Link>
        {CreateEditDialog}
      </>
    );
  }

  // ============================ PAGE VARIANT ============================
  // Lives on /app/upcoming-expenses. Full UI: list of cards with
  // Edit / Convert / Mark-paid / Delete actions, plus PDF export.
  if (loading && items.length === 0) {
    return <div className="text-sm text-[#6F828A] py-8 text-center" data-testid="upcoming-loading">Loading…</div>;
  }
  return (
    <div data-testid="upcoming-expenses-section">
      {/* Action row — Add + Export */}
      {!isMediator && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Button
            className="bg-[#1F9FA4] hover:bg-[#116B6E] text-white rounded-sm"
            data-testid="add-upcoming-expense-inline-btn"
            onClick={openCreate}
          >
            <Plus className="w-4 h-4 mr-1.5" /> Flag upcoming expense
          </Button>
          {items.length > 0 && (
            <Button
              variant="outline"
              className="border-[#D9E1E3] text-[#116B6E] hover:bg-[#F0F8F8] rounded-sm"
              data-testid="upcoming-export-btn"
              onClick={() => setExportOpen(true)}
            >
              <Download className="w-4 h-4 mr-1.5" /> Export PDF
            </Button>
          )}
        </div>
      )}

      {items.length === 0 ? (
        <div className="border border-dashed border-[#D9E1E3] bg-[#FBFCFC] rounded-md px-5 py-10 text-center" data-testid="upcoming-empty">
          <CalendarClock className="w-8 h-8 text-[#B88A3A] mx-auto mb-3" aria-hidden />
          <h3 className="font-heading text-base font-medium text-[#2E3A3F] mb-1">No upcoming expenses flagged</h3>
          <p className="text-sm text-[#6F828A] max-w-md mx-auto leading-relaxed">
            Use this to flag costs you can see coming — school fees, dental work, sports clubs, summer camp.
            You can share them with your co-parent and convert them into tracked expenses once paid.
          </p>
        </div>
      ) : (
        <ul className="space-y-2.5" data-testid="upcoming-expenses-list">
          {items.map((item) => {
            const isConverted = !!item.converted_to_expense_id;
            const isPaidNoExpense = !!item.marked_paid_at && !item.converted_to_expense_id;
            const isSettled = isConverted || isPaidNoExpense;
            return (
              <li
                key={item.id}
                className={`rounded-md border bg-white px-3.5 py-3 sm:py-3.5 flex flex-col sm:flex-row sm:items-start gap-3 ${isSettled ? "border-[#D9E1E3] opacity-70" : "border-[#D9E1E3]"}`}
                data-testid={`upcoming-item-${item.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-x-2 gap-y-1 flex-wrap">
                    <span className="font-medium text-[#2E3A3F] break-words">{item.title}</span>
                    <span className="font-mono-data text-sm text-[#116B6E]">{moneyFmt.format(item.amount)}</span>
                  </div>
                  <div className="text-[11px] text-[#6F828A] mt-0.5">
                    {formatDate(item.expected_date)} ({daysUntil(item.expected_date)})
                  </div>
                  <div className="flex items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] text-[#6F828A] flex-wrap">
                    {item.shared_with_coparent ? (
                      <span className="inline-flex items-center gap-1 text-[#116B6E]" data-testid={`upcoming-shared-${item.id}`}>
                        <Share2 className="w-3 h-3" /> Shared
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1" data-testid={`upcoming-private-${item.id}`}>
                        <EyeOff className="w-3 h-3" /> Private
                      </span>
                    )}
                    {item.reminder_date && (
                      <span>Reminder {formatDate(item.reminder_date)}</span>
                    )}
                    {Array.isArray(item.document_ids) && item.document_ids.length > 0 && (
                      <span>{item.document_ids.length} attachment{item.document_ids.length === 1 ? "" : "s"}</span>
                    )}
                    {Array.isArray(item.child_ids) && item.child_ids.length > 0 && childList.length > 0 && (
                      <span>
                        {item.child_ids.map((cid) => childList.find((c) => c.id === cid)?.name).filter(Boolean).join(", ")}
                      </span>
                    )}
                  </div>
                  {item.notes && (
                    <p className="text-xs text-[#2E3A3F] mt-1.5 break-words leading-relaxed">{item.notes}</p>
                  )}
                  {isConverted && (
                    <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#116B6E] bg-[#E6F2F3] border border-[#BFE0E1] px-2 py-1 rounded-sm" data-testid={`upcoming-converted-${item.id}`}>
                      <FileCheck2 className="w-3 h-3" /> Converted to expense
                      <Link to="/app/expenses" className="inline-flex items-center gap-0.5 underline hover:no-underline" data-testid={`upcoming-view-expense-${item.id}`}>
                        view <ExternalLink className="w-2.5 h-2.5" />
                      </Link>
                    </div>
                  )}
                  {isPaidNoExpense && (
                    <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#6F4E1E] bg-[#FBF0DC] border border-[#E0C88C] px-2 py-1 rounded-sm" data-testid={`upcoming-paid-${item.id}`}>
                      <CheckCircle2 className="w-3 h-3" /> Marked as paid (no expense)
                    </div>
                  )}
                </div>
                {!isMediator && (
                  <div className="flex items-center gap-1.5 flex-wrap sm:flex-shrink-0">
                    {!isSettled && (
                      <Button
                        size="sm"
                        className="bg-[#1F9FA4] hover:bg-[#116B6E] text-white rounded-sm h-8 px-2.5"
                        onClick={() => handleConvert(item)}
                        data-testid={`upcoming-convert-${item.id}`}
                        title="Create a tracked expense (with receipt + split)"
                      >
                        <ArrowRightCircle className="w-3.5 h-3.5 mr-1" /> Convert
                      </Button>
                    )}
                    {!isSettled && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#F0F8F8] rounded-sm h-8 px-2"
                        onClick={() => openEdit(item)}
                        data-testid={`upcoming-edit-${item.id}`}
                        title="Edit"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {!isSettled && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-[#D9E1E3] text-[#6F828A] hover:bg-[#F0F8F8] rounded-sm h-8 px-2"
                        onClick={() => handleMarkPaid(item)}
                        data-testid={`upcoming-mark-paid-${item.id}`}
                        title="Mark as paid without creating an expense"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark paid
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-[#6F828A] hover:text-[#C0291D]"
                      onClick={() => handleDelete(item)}
                      data-testid={`upcoming-delete-${item.id}`}
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {CreateEditDialog}
      {ExportDialog}
    </div>
  );
});
